import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Plus, Eye, Trash2, Lock, Unlock, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

function getAgeingClass(status) {
  switch (status) {
    case 'initial': return 'ageing-initial';
    case 'critical': return 'ageing-critical';
    case 'highly_critical': return 'ageing-highly-critical';
    case 'payment_stop': return 'ageing-payment-stop';
    case 'escalated': return 'ageing-escalated';
    default: return 'ageing-initial';
  }
}

function getAgeingLabel(status) {
  switch (status) {
    case 'initial': return '< 10d';
    case 'critical': return '10-20d';
    case 'highly_critical': return '20-35d';
    case 'payment_stop': return '35-50d';
    case 'escalated': return '> 50d';
    default: return status;
  }
}

export function POList() {
  const [pos, setPOs] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [buyerFilter, setBuyerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPO, setExpandedPO] = useState(null);
  const [poChallans, setPoChallans] = useState({}); // { po_number: [{challan_number, dispatch_date, items:[{sku,size,qty}]}, ...] }
  const [poChallansLoading, setPoChallansLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const { user } = useAuth();
  const userRole = user?.role || 'supervisor';
  const canManage = userRole === 'admin' || userRole === 'manager';
  const navigate = useNavigate();

  const fetchData = useCallback(async (page = currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (buyerFilter && buyerFilter !== 'all') params.append('buyer', buyerFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', page);
      params.append('limit', 25);

      const [posRes, buyersRes] = await Promise.all([
        axios.get(`${API}/api/po?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/buyers`, { withCredentials: true })
      ]);
      
      setPOs(posRes.data.pos || posRes.data);
      setTotalPages(posRes.data.pages || 1);
      setTotalCount(posRes.data.total || 0);
      setBuyers(buyersRes.data);
    } catch {
      // Failed to fetch data
    } finally {
      setLoading(false);
    }
  }, [search, buyerFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  // Fetch dispatched challans for the expanded PO (lazy, cached per PO)
  useEffect(() => {
    if (!expandedPO || poChallans[expandedPO]) return;
    let active = true;
    setPoChallansLoading(expandedPO);
    axios.get(`${API}/api/dispatches?po_number=${encodeURIComponent(expandedPO)}&limit=10000`, { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        const rows = (Array.isArray(data) ? data : []).filter(d => d.po_number === expandedPO);
        const grouped = {};
        rows.forEach(d => {
          const cn = d.challan_number || '—';
          if (!grouped[cn]) grouped[cn] = { challan_number: cn, dispatch_date: d.dispatch_date || '', items: [] };
          grouped[cn].items.push({ sku: d.sku, size: d.size, qty: d.dispatch_qty });
          if (!grouped[cn].dispatch_date && d.dispatch_date) grouped[cn].dispatch_date = d.dispatch_date;
        });
        const list = Object.values(grouped).sort((a, b) => (b.dispatch_date || '').localeCompare(a.dispatch_date || ''));
        setPoChallans(prev => ({ ...prev, [expandedPO]: list }));
      })
      .catch(() => { if (active) setPoChallans(prev => ({ ...prev, [expandedPO]: [] })); })
      .finally(() => { if (active) setPoChallansLoading(null); });
    return () => { active = false; };
  }, [expandedPO, poChallans]);

  const handleClosePO = async (po) => {
    setActionLoading(po.id);
    try {
      await axios.put(`${API}/api/po/${po.id}/close`, {}, { withCredentials: true });
      toast.success(`PO ${po.po_number} closed`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close PO');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenPO = async (po) => {
    setActionLoading(po.id);
    try {
      await axios.put(`${API}/api/po/${po.id}/open`, {}, { withCredentials: true });
      toast.success(`PO ${po.po_number} reopened`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen PO');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDeletePO = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.id);
    try {
      await axios.delete(`${API}/api/po/${deleteTarget.id}`, { withCredentials: true });
      toast.success(`PO ${deleteTarget.po_number} deleted`);
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete PO');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-6" data-testid="po-list-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Purchase Orders</h1>
          <p className="text-sm text-zinc-500">Manage all purchase orders and track dispatch status</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/po/new')}
            className="bg-[#09090B] text-white hover:bg-[#27272A] rounded-sm h-9"
            data-testid="new-po-button"
          >
            <Plus size={16} className="mr-1" />
            New PO
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6 bg-white border border-zinc-200 p-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search PO number, buyer, or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 rounded-sm"
              data-testid="po-search-input"
            />
          </div>
        </div>
        
        <div className="w-48">
          <Select value={buyerFilter} onValueChange={setBuyerFilter}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="buyer-filter">
              <SelectValue placeholder="All Buyers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Buyers</SelectItem>
              {buyers.map(buyer => (
                <SelectItem key={buyer} value={buyer}>{buyer}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-40">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500">Loading...</div>
        ) : pos.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-zinc-500 mb-4">No purchase orders found</p>
            <Button onClick={() => navigate('/po/new')} variant="outline" className="rounded-sm">
              Create your first PO
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="po-table">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Buyer</th>
                  <th>Order Date</th>
                  <th className="text-right">Ordered</th>
                  <th className="text-right">Dispatched</th>
                  <th className="text-right">Balance</th>
                  <th>Ageing</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pos.map((po, idx) => (
                  <React.Fragment key={po.po_number}>
                    <tr 
                      className={idx % 2 === 0 ? '' : 'table-row-even'}
                      data-testid={`po-row-${po.po_number}`}
                    >
                      <td className="font-mono font-medium">{po.po_number}</td>
                      <td>{po.buyer_name}</td>
                      <td className="font-mono text-zinc-500">{po.order_date}</td>
                      <td className="number-cell font-mono">{po.total_ordered?.toLocaleString()}</td>
                      <td className="number-cell font-mono">{po.total_dispatched?.toLocaleString()}</td>
                      <td className="number-cell font-mono font-medium">{po.total_balance?.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${getAgeingClass(po.ageing_status)}`}>
                          {po.ageing_days}d ({getAgeingLabel(po.ageing_status)})
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${po.status === 'completed' ? 'status-completed' : po.status === 'closed' ? 'bg-zinc-200 text-zinc-600' : 'status-pending'}`}>
                          {po.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedPO(expandedPO === po.po_number ? null : po.po_number)}
                            className="h-7 w-7 p-0"
                            title="View Details"
                            data-testid={`expand-po-${po.po_number}`}
                          >
                            <Eye size={14} />
                          </Button>
                          {canManage && (
                            <>
                              {po.status !== 'closed' && po.status !== 'completed' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleClosePO(po)}
                                  disabled={actionLoading === po.id}
                                  className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                  title="Close PO"
                                  data-testid={`close-po-${po.po_number}`}
                                >
                                  <Lock size={14} />
                                </Button>
                              ) : po.status === 'closed' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenPO(po)}
                                  disabled={actionLoading === po.id}
                                  className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-50"
                                  title="Reopen PO"
                                  data-testid={`open-po-${po.po_number}`}
                                >
                                  <Unlock size={14} />
                                </Button>
                              ) : null}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setDeleteTarget(po); setDeleteModal(true); }}
                                disabled={actionLoading === po.id}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Delete PO"
                                data-testid={`delete-po-${po.po_number}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedPO === po.po_number && (
                      <tr className="bg-zinc-50">
                        <td colSpan={9} className="p-4">
                          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Line Items</div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                                <th className="pb-2">SKU</th>
                                <th className="pb-2">Size</th>
                                <th className="pb-2 text-right">Ordered</th>
                                <th className="pb-2 text-right">Dispatched</th>
                                <th className="pb-2 text-right">Balance</th>
                                <th className="pb-2">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {po.line_items.map((item) => (
                                <tr key={`${item.sku}-${item.size}`} className="border-t border-zinc-200">
                                  <td className="py-2 font-mono">{item.sku}</td>
                                  <td className="py-2 font-mono">{item.size}</td>
                                  <td className="py-2 text-right font-mono">{item.ordered_qty}</td>
                                  <td className="py-2 text-right font-mono">{item.dispatched_qty || 0}</td>
                                  <td className="py-2 text-right font-mono font-medium">{item.balance_qty || item.ordered_qty}</td>
                                  <td className="py-2">
                                    <span className={`status-badge text-[10px] ${item.item_status === 'completed' ? 'status-completed' : 'status-pending'}`}>
                                      {item.item_status || 'pending'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {/* Dispatched Challans for this PO */}
                          <div className="mt-6">
                            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Dispatched Challans</div>
                            {poChallansLoading === po.po_number && (
                              <div className="text-xs text-zinc-400 py-2">Loading…</div>
                            )}
                            {poChallansLoading !== po.po_number && (poChallans[po.po_number]?.length === 0) && (
                              <div className="text-xs text-zinc-400 py-2">No dispatches yet for this PO.</div>
                            )}
                            {(poChallans[po.po_number] || []).map((ch) => (
                              <div key={ch.challan_number} className="mb-3 border border-zinc-200 rounded-sm bg-white" data-testid={`po-challan-${po.po_number}-${ch.challan_number}`}>
                                <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                                  <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs font-semibold">{ch.challan_number}</span>
                                    <span className="text-[10px] text-zinc-500">{ch.dispatch_date}</span>
                                  </div>
                                  <span className="text-[10px] font-mono text-zinc-600">
                                    Total: {ch.items.reduce((s, i) => s + (i.qty || 0), 0)} pairs
                                  </span>
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                                      <th className="py-1.5 px-3">SKU</th>
                                      <th className="py-1.5 px-3 w-16">Size</th>
                                      <th className="py-1.5 px-3 w-24 text-right">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ch.items.map((it, idx) => (
                                      <tr key={`${ch.challan_number}-${it.sku}-${it.size}-${idx}`} className="border-t border-zinc-100">
                                        <td className="py-1.5 px-3 font-mono">{it.sku}</td>
                                        <td className="py-1.5 px-3 font-mono">{it.size}</td>
                                        <td className="py-1.5 px-3 text-right font-mono">{it.qty}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-zinc-200" data-testid="po-pagination">
            <span className="text-sm text-zinc-500">
              Showing {((currentPage - 1) * 25) + 1}–{Math.min(currentPage * 25, totalCount)} of {totalCount} POs
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
                className="h-8 rounded-sm"
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 : currentPage + i - 2;
                if (pageNum < 1 || pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`h-8 w-8 rounded-sm ${pageNum === currentPage ? 'bg-[#09090B]' : ''}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                className="h-8 rounded-sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete PO Confirmation Dialog */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent className="max-w-sm" data-testid="delete-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={18} />
              Delete Purchase Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-700">
              Are you sure you want to delete PO <strong className="font-mono">{deleteTarget?.po_number}</strong>?
              This action cannot be undone.
            </p>
            <p className="text-xs text-zinc-500">
              Note: POs with existing dispatches cannot be deleted. Remove associated challans first.
            </p>
            <div className="flex justify-end gap-3 pt-2 border-t border-zinc-200">
              <Button variant="outline" className="h-9 rounded-sm" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                className="h-9 rounded-sm bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDeletePO}
                disabled={actionLoading === deleteTarget?.id}
                data-testid="confirm-delete-po"
              >
                {actionLoading === deleteTarget?.id ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
